import { HHStack, HHTypography } from '@hinge-health/react-component-library';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { GetSplitTreatmentOnOrOff } from '../../../../../../../components/splits';
import {
  ORTHOPEDIC_SPECIALIST_VIDEO_VISIT_KEY,
  ORTHO_SPECIALIST_VIDEO_VISIT_BILLABLE_ACTIVITY_TREATMENT,
} from '../../../../../constants/strings/contract/form-constants';
import { ContractType, EngagementType } from '../../../../../types';
import {
  getBillableActivitiesFromContractPackageOrDefault,
  getFormErrorsForInnerField,
  innerFieldHasErrors,
} from '../../../../../utils/contract-utils';
import { PackageFormData } from '../../custom-package-types';
import { mapEngagementTypesToRules } from '../engagement-types-rules';
import BillableActivitiesWithSubTypes from './billable-activities-with-sub-types';

export interface BlacklistEngagementType {
  [key: string]: null | string;
}

export interface BillableActivitiesProps {
  wholeFormDisabled: boolean | undefined;
  engagementTypes: EngagementType[];
  direction?: string;
  index?: number;
  existingContract?: ContractType;
  defaultCheckedEngagementType?: string;
  blacklistEngagementTypes?: BlacklistEngagementType[];
}

export const BillableActivities = ({
  wholeFormDisabled,
  engagementTypes,
  direction,
  index = 0,
  existingContract,
  defaultCheckedEngagementType,
  blacklistEngagementTypes,
}: BillableActivitiesProps): JSX.Element => {
  const {
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = useFormContext<PackageFormData>();

  const watchedBillingModelId = watch(
    `selectedPackages.${index}.billingModelId`,
  );

  const watchedBillableActivities = watch(
    `selectedPackages.${index}.selectedBillableActivities`,
  );

  const orthoVideoVisitTreatmentOn = GetSplitTreatmentOnOrOff(
    ORTHO_SPECIALIST_VIDEO_VISIT_BILLABLE_ACTIVITY_TREATMENT,
  );

  const visibleEngagementTypes = orthoVideoVisitTreatmentOn
    ? engagementTypes
    : engagementTypes.filter(
        et => et.name !== ORTHOPEDIC_SPECIALIST_VIDEO_VISIT_KEY,
      );

  const engagementTypesWithRules = mapEngagementTypesToRules(
    visibleEngagementTypes,
    blacklistEngagementTypes,
    defaultCheckedEngagementType,
  );

  useEffect(() => {
    setValue(
      `selectedPackages.${index}.selectedBillableActivities`,
      getBillableActivitiesFromContractPackageOrDefault(
        engagementTypesWithRules,
        index,
        existingContract,
      ),
      { shouldValidate: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedBillingModelId]);

  useEffect(() => {
    if (watchedBillableActivities) {
      trigger('selectedPackages');
    }
  }, [watchedBillableActivities, trigger]);

  if (visibleEngagementTypes.length === 0) {
    return (
      <HHTypography hhVariant="body" sx={{ color: 'red' }}>
        billable activities unable to load
      </HHTypography>
    );
  }

  return (
    <HHStack data-testid="billable-activities-container">
      <HHTypography hhVariant="caption" color={'GrayText'}>
        Billable Activities
      </HHTypography>
      {innerFieldHasErrors('selectedBillableActivities', index, errors) && (
        <HHTypography
          data-testid="billable-activities-validation-error"
          hhVariant="variant-bypass"
          sx={{ color: (theme): string => theme.palette.error.main }}
          variant="caption"
        >
          {getFormErrorsForInnerField(
            'selectedBillableActivities',
            index,
            errors,
          )}
        </HHTypography>
      )}
      <BillableActivitiesWithSubTypes
        wholeFormDisabled={wholeFormDisabled}
        engagementTypes={engagementTypesWithRules}
        direction={direction}
        index={index}
      />
    </HHStack>
  );
};
